import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Loader, Message, Segment } from 'semantic-ui-react';
import { Notification, STATUS_TYPES } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import { useConfirm } from 'shared/confirmation/useConfirm';
import PipelineEditor, { PipelinesHeader } from './PipelineEditor';
import CustomFields from './CustomFields';
import ConfirmCrmSetup from './ConfirmCrmSetup';
import { CRM_CONFIGURATION_SCREENS } from '../constants';
import { findDuplicateIds, getLengthError, hasErrors } from '../validationUtils';

const PIPELINE_RECOMMENDATION_ID = 'lead_opportunity_pipelines';

// Mirrors Settings → Pipeline: name 2-32 characters, required unique stage names, positive whole-number days
const validatePipeline = ({ included, name, stages }) => {
    if (!included) {
        return {};
    }
    const errors = {};
    const nameError = getLengthError(name, 'Pipeline name', 2, 32);
    if (nameError) {
        errors.name = nameError;
    }
    const duplicateStageIds = findDuplicateIds(stages, ({ name }) => name);
    const stageErrors = stages.reduce((result, stage) => {
        const stageError = {};
        const stageNameError = getLengthError(stage.name, 'Stage name', 1, 64) || (duplicateStageIds.includes(stage.id) && 'Stage names must be unique');
        if (stageNameError) {
            stageError.name = stageNameError;
        }
        if (!Number.isInteger(stage.days) || stage.days < 1) {
            stageError.days = 'Enter a whole number of 1 or more';
        }
        return Object.keys(stageError).length ? { ...result, [stage.id]: stageError } : result;
    }, {});
    if (Object.keys(stageErrors).length) {
        errors.stages = stageErrors;
    }
    return errors;
}

const validateFieldGroup = ({ name, fields }) => {
    const selectedFields = fields.filter(({ selected }) => selected);
    if (!selectedFields.length) {
        return {};
    }
    const errors = {};
    const nameError = getLengthError(name, 'Group name', 2, 64);
    if (nameError) {
        errors.name = nameError;
    }
    const duplicateLabelIds = findDuplicateIds(selectedFields, ({ label }) => label);
    const fieldErrors = selectedFields.reduce((result, field) => {
        const fieldError = {};
        const labelError = getLengthError(field.label, 'Label', 2, 64) || (duplicateLabelIds.includes(field.id) && 'Field labels must be unique');
        if (labelError) {
            fieldError.label = labelError;
        }
        if (field.field_type === 'dropdown') {
            const uniqueOptions = new Set(field.options.map(option => option.toLowerCase()));
            if (field.options.length < 2) {
                fieldError.options = 'Add at least 2 options';
            } else if (uniqueOptions.size !== field.options.length) {
                fieldError.options = 'Options must be unique';
            }
        }
        return Object.keys(fieldError).length ? { ...result, [field.id]: fieldError } : result;
    }, {});
    if (Object.keys(fieldErrors).length) {
        errors.fields = fieldErrors;
    }
    return errors;
}


const CrmConfigurationStep = ({ businessProfileDraft, draft, onDraftChange, onBack, onComplete }) => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();

    const { user_id, isGeneratingCrmSetup } = useSelector(state => ({
        user_id: state.auth.user_id,
        isGeneratingCrmSetup: state.onboarding.isGeneratingCrmSetup
    }));

    const [screen, setScreen] = useState(draft?.approval ? CRM_CONFIGURATION_SCREENS.CONFIRM : (draft?.screen || CRM_CONFIGURATION_SCREENS.PIPELINES));
    const [pipelines, setPipelines] = useState(draft?.pipelines || null);
    const [fieldGroup, setFieldGroup] = useState(draft?.field_group || null);
    const [explanations, setExplanations] = useState(draft?.explanations || {});
    const [generatedFor, setGeneratedFor] = useState(draft?.generated_for || null);
    const [approval, setApproval] = useState(draft?.approval || null);

    const profile = businessProfileDraft?.profile || {};
    // Pipelines start on only if they were picked in Business Profile (or that choice wasn't made yet)
    const businessRecommendations = businessProfileDraft?.recommendations || [];
    const includePipelines = !businessRecommendations.some(({ id }) => id === PIPELINE_RECOMMENDATION_ID)
        || (businessProfileDraft?.selected || []).includes(PIPELINE_RECOMMENDATION_ID);
    const inputsJSON = JSON.stringify({ industry: profile.industry || '', primary_goal: profile.primary_goal || '' });
    const isStale = !!generatedFor && JSON.stringify(generatedFor) !== inputsJSON;

    const generateSetup = useCallback(async () => {
        try {
            const inputs = JSON.parse(inputsJSON);
            const result = await dispatch(onboardingActions.generateCrmSetup({ ...inputs, include_pipelines: includePipelines }));
            setPipelines(result.pipelines);
            setFieldGroup(result.field_group);
            setExplanations(result.explanations);
            setGeneratedFor(inputs);
            setApproval(null);
        } catch (e) {
            Notification.alert('Failed to generate CRM suggestions', STATUS_TYPES.ERROR);
        }
    }, [dispatch, inputsJSON, includePipelines]);

    // Suggestions are generated automatically the first time the step opens
    const hasSuggestions = !!pipelines && !!fieldGroup;
    useEffect(() => {
        if (!hasSuggestions) {
            generateSetup();
        }
    }, [hasSuggestions, generateSetup]);

    // An approval only covers what the user saw; any later edit requires approving again
    const approvalSnapshot = JSON.stringify({ pipelines, fieldGroup });
    useEffect(() => {
        if (approval && approval.snapshot !== approvalSnapshot) {
            setApproval(null);
        }
    }, [approval, approvalSnapshot]);

    const getStepDraft = (overrides = {}) => ({
        screen,
        pipelines,
        field_group: fieldGroup,
        explanations,
        generated_for: generatedFor,
        approval,
        ...overrides
    });

    useEffect(() => {
        onDraftChange({ screen, pipelines, field_group: fieldGroup, explanations, generated_for: generatedFor, approval });
    }, [onDraftChange, screen, pipelines, fieldGroup, explanations, generatedFor, approval]);

    const onRegenerate = async () => {
        const shouldRegenerate = await isConfirmed(
            'Regenerate CRM suggestions?',
            'New suggestions will replace your pipelines and contact fields.'
        );
        if (shouldRegenerate) {
            await generateSetup();
        }
    }

    const onConfirm = () => {
        // TODO: send the approved CRM setup to the backend once the endpoint exists
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: approvalSnapshot };
        setApproval(newApproval);
        onComplete(getStepDraft({ approval: newApproval }));
        Notification.alert('CRM setup saved as a draft', STATUS_TYPES.SUCCESS);
    }

    if (!hasSuggestions) {
        return (
            <Segment placeholder textAlign='center'>
                {isGeneratingCrmSetup ? (
                    <Loader active inline='centered'>Suggesting pipelines and contact fields for your business…</Loader>
                ) : (
                    <p>We couldn't generate CRM suggestions. <Button basic size='small' onClick={generateSetup}>Try Again</Button></p>
                )}
            </Segment>
        );
    }

    const leadErrors = validatePipeline(pipelines.lead);
    const opportunityErrors = validatePipeline(pipelines.opportunity);
    const fieldGroupErrors = validateFieldGroup(fieldGroup);

    switch (screen) {
        case CRM_CONFIGURATION_SCREENS.FIELDS:
            return (
                <CustomFields
                    fieldGroup={fieldGroup}
                    errors={fieldGroupErrors}
                    explanation={explanations.fields}
                    onChange={setFieldGroup}
                    canContinue={!hasErrors(fieldGroupErrors)}
                    onBack={() => setScreen(CRM_CONFIGURATION_SCREENS.PIPELINES)}
                    onContinue={() => setScreen(CRM_CONFIGURATION_SCREENS.CONFIRM)}
                />
            );
        case CRM_CONFIGURATION_SCREENS.CONFIRM:
            return (
                <ConfirmCrmSetup
                    pipelines={pipelines}
                    fieldGroup={fieldGroup}
                    isApprovedInitially={!!approval}
                    onBack={() => setScreen(CRM_CONFIGURATION_SCREENS.FIELDS)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <Segment loading={isGeneratingCrmSetup}>
                    <PipelinesHeader explanation={explanations.pipelines} />
                    {isStale && (
                        <Message warning size='small'>
                            Your business profile changed since these suggestions were made.
                            <Button size='tiny' basic compact style={{ marginLeft: '1em' }} onClick={onRegenerate}>Regenerate Suggestions</Button>
                        </Message>
                    )}
                    <PipelineEditor
                        title='Lead Pipeline'
                        description='Tracks new leads until they are qualified.'
                        pipeline={pipelines.lead}
                        errors={leadErrors}
                        onChange={(lead) => setPipelines({ ...pipelines, lead })}
                    />
                    <PipelineEditor
                        title='Opportunity Pipeline'
                        description='Tracks qualified deals until they are won or lost.'
                        pipeline={pipelines.opportunity}
                        errors={opportunityErrors}
                        onChange={(opportunity) => setPipelines({ ...pipelines, opportunity })}
                    />
                    {!pipelines.lead.included && !pipelines.opportunity.included && (
                        <Message info size='small'>DYL's default pipelines will be used. You can change them anytime in Settings → Pipeline.</Message>
                    )}
                    <div className='Onboarding__footer'>
                        <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                        <Button primary floated='right' type='button' disabled={hasErrors(leadErrors) || hasErrors(opportunityErrors)} onClick={() => setScreen(CRM_CONFIGURATION_SCREENS.FIELDS)}>
                            Continue to Contact Fields <Icon name='arrow right' />
                        </Button>
                        <div style={{ clear: 'both' }} />
                    </div>
                </Segment>
            );
    }
}

export default CrmConfigurationStep;
